import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  BellRing,
  Building2,
  Camera,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileBarChart,
  FileText,
  GitCompareArrows,
  History,
  LayoutDashboard,
  LogOut,
  LucideIcon,
  Menu,
  Package,
  PackageSearch,
  Search,
  ShieldCheck,
  Signature,
  Undo2,
  Upload,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { permissionService, PermissionCode } from '../services/permissionService';
import { notificationService } from '../services/notificationService';
import { BreadcrumbItem, NavigationSection } from '../utils/roleRouting';
import { isUnreadNotificationStatus, NOTIFICATION_READ_EVENT, NotificationReadEventDetail } from '../utils/notificationEvents';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  onLogout: () => Promise<void>;
  dashboardPath?: string;
  roleLabel?: string;
  sectionLabel?: string;
  userName?: string;
  navigationSections?: NavigationSection[];
  breadcrumbs?: BreadcrumbItem[];
  notificationCount?: number;
  notificationPath?: string;
  scopeLabel?: string;
  adminViewMode?: boolean;
  returnToAdminPath?: string;
  changeDepartmentPath?: string;
}

const ICONS: Record<string, LucideIcon> = {
  AlertTriangle,
  Bell,
  BellRing,
  Building2,
  Camera,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileBarChart,
  FileText,
  GitCompareArrows,
  History,
  LayoutDashboard,
  Package,
  PackageSearch,
  Search,
  ShieldCheck,
  Signature,
  Undo2,
  Upload,
  Users,
};

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  title = 'Administrator Dashboard',
  onLogout,
  dashboardPath = '/admin-dashboard',
  roleLabel = 'System Administrator',
  sectionLabel = 'Administration',
  userName = 'Administrator User',
  navigationSections,
  breadcrumbs,
  notificationCount,
  notificationPath,
  scopeLabel,
  adminViewMode,
  returnToAdminPath,
  changeDepartmentPath,
}) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [internalNotificationCount, setInternalNotificationCount] = React.useState(notificationCount || 0);
  const readNotificationIds = React.useRef(new Set<string>());
  const location = useLocation();
  const navigate = useNavigate();

  const fallbackNavigationSections: NavigationSection[] = dashboardPath === '/admin-dashboard' ? [
    {
      label: sectionLabel,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', path: dashboardPath, requiredPermissions: [] as PermissionCode[] },
        { id: 'users', label: 'User Management', icon: 'Users', path: `${dashboardPath}/users`, requiredPermissions: ['admin.user.manage'] as PermissionCode[] },
        { id: 'departments', label: 'Departments', icon: 'Building2', path: `${dashboardPath}/departments`, requiredPermissions: ['admin.user.manage'] as PermissionCode[] },
        { id: 'roles', label: 'Role Management', icon: 'ShieldCheck', path: `${dashboardPath}/roles`, requiredPermissions: ['admin.role.manage'] as PermissionCode[] },
        { id: 'system-governance', label: 'System Governance', icon: 'ShieldCheck', path: `${dashboardPath}/system-governance`, requiredPermissions: ['admin.dashboard.read'] as PermissionCode[] },
        { id: 'data-privacy', label: 'Data Privacy Compliance', icon: 'FileText', path: `${dashboardPath}/data-privacy`, requiredPermissions: ['admin.dashboard.read'] as PermissionCode[] },
        { id: 'archives', label: 'Archived Records', icon: 'History', path: `${dashboardPath}/archives`, requiredPermissions: ['records.archive.view'] as PermissionCode[] },
        { id: 'audit-logs', label: 'Audit Trail', icon: 'FileText', path: `${dashboardPath}/audit-logs`, requiredPermissions: ['admin.dashboard.read'] as PermissionCode[] },
        { id: 'security-center', label: 'Security Center', icon: 'ShieldCheck', path: `${dashboardPath}/security-center`, requiredPermissions: ['admin.dashboard.read'] as PermissionCode[] },
      ],
    },
  ] : [
    {
      label: sectionLabel,
      items: [{ id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', path: dashboardPath, requiredPermissions: [] as PermissionCode[] }],
    },
  ];

  const visibleNavigationSections = (navigationSections || fallbackNavigationSections)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        item.requiredPermissions.length === 0 || permissionService.hasAnyPermission(item.requiredPermissions)
      ),
    }))
    .filter((section) => section.items.length > 0);

  const handleLogout = async () => {
    await onLogout();
    navigate('/login', { replace: true });
  };

  const visibleMenuItems = visibleNavigationSections.flatMap((section) => section.items);
  const canViewNotifications = permissionService.hasPermission('notification.system.view');
  const displayedNotificationCount = notificationCount ?? internalNotificationCount;
  const stripQuery = (path: string): string => path.split('?')[0];

  React.useEffect(() => {
    if (typeof notificationCount === 'number') {
      setInternalNotificationCount(notificationCount);
    }
  }, [notificationCount]);

  React.useEffect(() => {
    let mounted = true;
    if (typeof notificationCount === 'number' || !canViewNotifications) return () => { mounted = false; };
    notificationService.getSystemNotifications()
      .then((items) => {
        if (mounted) setInternalNotificationCount(items.filter((item) => isUnreadNotificationStatus(item.status)).length);
      })
      .catch(() => {
        if (mounted) setInternalNotificationCount(0);
      });
    return () => { mounted = false; };
  }, [canViewNotifications, location.pathname, notificationCount]);

  React.useEffect(() => {
    const handleNotificationRead = (event: Event) => {
      const detail = (event as CustomEvent<NotificationReadEventDetail>).detail;
      if (!detail || detail.nextStatus !== 'read' || !isUnreadNotificationStatus(detail.previousStatus)) return;
      const key = String(detail.id);
      if (readNotificationIds.current.has(key)) return;
      readNotificationIds.current.add(key);
      setInternalNotificationCount((current) => Math.max(0, current - 1));
    };
    window.addEventListener(NOTIFICATION_READ_EVENT, handleNotificationRead);
    return () => window.removeEventListener(NOTIFICATION_READ_EVENT, handleNotificationRead);
  }, []);
  const getNotificationPath = () => notificationPath || (
    dashboardPath === '/admin-dashboard'
      ? '/admin-dashboard/security-center'
      : `${dashboardPath}/notifications`
  );

  const currentItem = visibleMenuItems.find((item) => {
    const itemPath = stripQuery(item.path);
    return itemPath === dashboardPath
      ? location.pathname === itemPath
      : location.pathname.startsWith(itemPath);
  });
  const activeBreadcrumbs = breadcrumbs?.length
    ? breadcrumbs
    : [
      { label: roleLabel, path: dashboardPath },
      { label: currentItem?.label || title },
    ];

  return (
    <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="brand-lockup">
          <img src="/cybershield-logo-transparent.png" alt="CyberShield" />
          <button className="sidebar-toggle" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}><Menu size={18} /></button>
          <button className="mobile-nav-close" onClick={() => setSidebarOpen(false)} aria-label="Close navigation"><X size={18} /></button>
        </div>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          {visibleNavigationSections.map((section) => (
            <div className="nav-section" key={section.label}>
              <p className="nav-section-label">{section.label}</p>
              {section.items.map((item) => {
                const Icon = ICONS[item.icon] || LayoutDashboard;
                const itemPath = stripQuery(item.path);
                const active = itemPath === dashboardPath
                  ? location.pathname === itemPath
                  : location.pathname.startsWith(itemPath);
                return (
                  <Link key={item.id} to={item.path} className={active ? 'active' : ''} onClick={() => setSidebarOpen(false)} title={collapsed ? item.label : undefined}>
                    <Icon className="nav-icon" size={18} aria-hidden="true" />
                    <span className="nav-label">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="user-avatar">{userName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}<span /></div>
          <div className="sidebar-user-copy"><strong>{userName}</strong><span>{roleLabel}</span></div>
          <ChevronRight className="sidebar-user-chevron" size={14} />
        </div>
        {adminViewMode && returnToAdminPath && (
          <Link to={returnToAdminPath} className="sidebar-logout" onClick={() => setSidebarOpen(false)}>
            <span>Return to Administrator Workspace</span>
          </Link>
        )}
        {adminViewMode && changeDepartmentPath && (
          <Link to={changeDepartmentPath} className="sidebar-logout" onClick={() => setSidebarOpen(false)}>
            <span>Change Department</span>
          </Link>
        )}
        <Link to="/profile" className="sidebar-logout" onClick={() => setSidebarOpen(false)}>
          <UserCircle size={15} /><span>My Profile</span>
        </Link>
        <Link to="/privacy-agreement" className="sidebar-logout" onClick={() => setSidebarOpen(false)}>
          <FileText size={15} /><span>Data Privacy Agreement</span>
        </Link>
        <button className="sidebar-logout" onClick={handleLogout}><LogOut size={15} /><span>Logout</span></button>
      </aside>

      {sidebarOpen && <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" />}

      <div className="app-workspace">
        <header className="topbar">
          <button className="menu-button" onClick={() => setSidebarOpen((open) => !open)} aria-label="Toggle navigation"><Menu size={18} /></button>
          <div className="breadcrumbs" aria-label="Breadcrumb">
            {activeBreadcrumbs.map((crumb, index) => (
              <React.Fragment key={`${crumb.label}-${index}`}>
                {index > 0 && <ChevronRight size={12} />}
                {crumb.path && index < activeBreadcrumbs.length - 1 ? (
                  <Link to={crumb.path}>{crumb.label}</Link>
                ) : (
                  <strong>{crumb.label}</strong>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="topbar-actions">
            {scopeLabel && <span className="role-chip">{scopeLabel}</span>}
            <span className="role-chip">{roleLabel.split(/\s+/).map((part) => part[0]).join('').slice(0, 3).toUpperCase()} <span>{roleLabel}</span></span>
            {canViewNotifications && (
              <button
                className="icon-button notification-button"
                type="button"
                aria-label="Open notifications"
                onClick={() => navigate(getNotificationPath())}
              >
                <Bell size={17} />
                {displayedNotificationCount > 0 && <span className="notification-badge">{displayedNotificationCount > 99 ? '99+' : displayedNotificationCount}</span>}
              </button>
            )}
          </div>
        </header>
        <main className="page-canvas">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
